/* Amberden — Your rooms (2026-09-08).
 *
 * Three small things, one file, no dependencies:
 *
 *   1. THE BOARD. sections/amberden-rooms-board.liquid renders its blocks in
 *      editor order as a flat list; this places each tile into the shortest
 *      column in turn, Pinterest's own method, so the order Arnav drags in the
 *      editor is the order a reader meets on every screen. CSS columns cannot
 *      do that: they fill top to bottom, so block 2 lands under block 1. Two
 *      columns under 750px, the section's setting above it; re-laid when the
 *      breakpoint flips. Heights are estimated from the images' own width and
 *      height attributes, so nothing waits for a download and nothing shifts.
 *      Without JS the CSS fallback is columns, and the page still reads.
 *
 *   2. THE QUICK LOOK. One <dialog> holding one panel per lamp; the tap on a
 *      tile shows the panel for that lamp. Add to cart is Horizon's own
 *      product-form-component; after it submits, the dialog closes, because
 *      assets/cart-drawer.js waits for an open modal to close before it opens
 *      the drawer (so the drawer's focus restoration lands in the right place).
 *
 *   3. THE CAROUSEL on the product page: a counter and two arrows above the
 *      track, and a lightbox for the photograph — there is nothing to buy from
 *      there, you are already on the lamp's page.
 *
 * Scroll and click handlers are delegated from document, so a section
 * re-rendered by the theme editor keeps working without re-binding.
 */

(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduce ? 'auto' : 'smooth';

  /* ---- 1. The board ------------------------------------------------------ */
  const laidOut = new WeakSet();

  const layoutBoard = (board) => {
    const tiles = [...board.querySelectorAll('[data-amb-tile]')];
    if (!tiles.length) return;
    const wide = matchMedia('(min-width: 750px)').matches;
    const n = wide
      ? Number(board.dataset.ambBoardCols) || 5
      : Number(board.dataset.ambBoardColsMobile) || 2;
    if (board.dataset.ambBoardLaid === String(n)) return;

    const columns = Array.from({ length: n }, () => {
      const col = document.createElement('div');
      col.className = 'amb-board__col';
      return col;
    });
    const heights = new Array(n).fill(0);

    for (const tile of tiles) {
      const img = tile.querySelector('img');
      const w = Number(img?.getAttribute('width')) || 4;
      const h = Number(img?.getAttribute('height')) || 5;
      const i = heights.indexOf(Math.min(...heights));
      columns[i].appendChild(tile);
      heights[i] += h / w;
    }

    board.replaceChildren(...columns);
    board.dataset.ambBoardLaid = String(n);
    laidOut.add(board);
  };

  const boards = () => document.querySelectorAll('[data-amb-board]');
  const layoutAll = () => boards().forEach(layoutBoard);

  layoutAll();
  matchMedia('(min-width: 750px)').addEventListener('change', layoutAll);
  document.addEventListener('shopify:section:load', layoutAll);

  /* ---- 2. The quick look ------------------------------------------------- */
  const dialogFor = () => document.querySelector('[data-amb-ql]');

  let quickLookOpener = null;

  const openQuickLook = (handle, opener) => {
    const dialog = dialogFor();
    if (!(dialog instanceof HTMLDialogElement)) return false;
    let found = null;
    for (const panel of dialog.querySelectorAll('[data-amb-ql-panel]')) {
      const match = panel.dataset.ambQlPanel === handle;
      panel.hidden = !match;
      if (match) found = panel;
    }
    if (!found) return false;
    if (!dialog.open) {
      quickLookOpener = opener instanceof HTMLElement ? opener : null;
      dialog.showModal();
      dialog.addEventListener('close', () => {
        const back = quickLookOpener;
        quickLookOpener = null;
        if (back && back.isConnected) back.focus({ preventScroll: true });
      }, { once: true });
    }
    found.scrollTop = 0;
    found.querySelector('[data-amb-ql-close]')?.focus({ preventScroll: true });
    return true;
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-amb-quick-open]');
    if (opener) {
      event.preventDefault();
      if (!openQuickLook(opener.dataset.ambQuickOpen, opener)) {
        // No panel for this lamp: fall through to its page.
        const tile = opener.closest('[data-amb-tile]');
        const link = tile?.querySelector('a[href]');
        if (link) window.location.href = link.href;
      }
      return;
    }

    const closer = target.closest('[data-amb-ql-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // A press on the backdrop: the dialog itself is the target, its panels are not.
    if (target.matches('dialog[data-amb-ql]')) target.close();
  });

  // Add to cart, two stores' worth of behaviour:
  //
  // When the cart drawer is set to open on its own (cart-drawer-component
  // carries `auto-open`), the dialog closes on the press, BEFORE the form
  // submits. assets/cart-drawer.js looks for an open modal dialog around the
  // cart event (`closest('dialog:modal')`) and, finding one, waits for that
  // dialog's close — but it registers that listener only after the add request
  // resolves, so a dialog closed in between is never heard and the drawer never
  // opens (measured on staging 2026-09-08: cart 0 → 1, drawer shut). Closed
  // first, the dialog is no longer :modal when the event fires, and the drawer
  // opens the way it does from a product page. The form still submits: closing
  // a dialog does not cancel the click's default action.
  //
  // When the drawer is NOT set to open on its own (this store today, on the
  // product page too), the panel stays: Horizon's button turns to its "Added"
  // checkmark and the header's cart count updates, and the visitor closes the
  // panel when they are done. Closing it for them would make the add silent.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-amb-ql-form] button[type="submit"]');
      if (!button) return;
      if (!document.querySelector('cart-drawer-component[auto-open]')) return;
      const dialog = button.closest('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    },
    true
  );

  /* ---- 3. The carousel and its lightbox ---------------------------------- */
  const carousels = new WeakSet();

  const bindCarousel = (section) => {
    if (carousels.has(section)) return;
    carousels.add(section);
    const track = section.querySelector('[data-amb-rooms-track]');
    const count = section.querySelector('[data-amb-rooms-count]');
    const prev = section.querySelector('[data-amb-rooms-prev]');
    const next = section.querySelector('[data-amb-rooms-next]');
    if (!track) return;
    const cards = () => [...track.children];

    const step = () => {
      const first = cards()[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const all = cards();
      const s = step();
      const i = s ? Math.min(all.length - 1, Math.round(track.scrollLeft / s)) : 0;
      if (count) count.textContent = all.length ? `${i + 1} / ${all.length}` : '';
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };

    track.addEventListener('scroll', update, { passive: true });
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const bindCarousels = () => document.querySelectorAll('[data-amb-rooms]').forEach(bindCarousel);
  bindCarousels();
  document.addEventListener('shopify:section:load', bindCarousels);

  let lightbox = null;
  const openLightbox = (src) => {
    if (!lightbox) {
      lightbox = document.createElement('dialog');
      lightbox.className = 'amb-lightbox';
      lightbox.setAttribute('aria-label', 'Photograph');
      lightbox.innerHTML =
        '<img class="amb-lightbox__img" alt="">' +
        '<button type="button" class="amb-lightbox__close" aria-label="Close">' +
        '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg></button>';
      document.body.appendChild(lightbox);
      lightbox.querySelector('button')?.addEventListener('click', () => lightbox.close());
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) lightbox.close();
      });
    }
    const img = lightbox.querySelector('img');
    if (img) img.src = src;
    lightbox.showModal();
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-amb-lightbox]');
    if (!button) return;
    event.preventDefault();
    openLightbox(button.dataset.ambLightbox);
  });
})();
